import {
  Descriptions,
  Header,
  IconAirfare,
  Space,
  SubText,
  Text,
} from '@procurify/ui'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { FormattedShortDate } from '@webapp/core/components/FormattedDate/FormattedShortDate'
import { Price } from '@webapp/core/components/Price'
import { UserLabel } from '@webapp/core/components/UserLabel'
import { useProcurifyIntl } from '@webapp/hooks'
import { TravelTransportTypes } from '@webapp/modules/expense/entities/enums/TravelTransportTypeEnum'
import { type TravelDestination } from '@webapp/modules/expense/entities/TravelDestination'
import { type TravelRequestDetail } from '@webapp/modules/expense/entities/TravelRequestDetail'
import messages from './messages'
import { StatusStampStyled } from './styles/StatusStampStyled'
import { TravelApprovalHistoryPopover } from './TravelApprovalHistoryPopover'

export interface ITravelDetailContentProps {
  travel: TravelRequestDetail
}

const TRANSPORT_MESSAGES = {
  [TravelTransportTypes.DRIVE]: messages.transportDriveLabel,
  [TravelTransportTypes.FLIGHT]: messages.transportFlightLabel,
  [TravelTransportTypes.RAIL]: messages.transportRailLabel,
  [TravelTransportTypes.SAIL]: messages.transportSailLabel,
} as const

export const TravelDetailContent = ({ travel }: ITravelDetailContentProps) => {
  const intl = useProcurifyIntl()

  const itineraryColumns = [
    col('BASIC', {
      label: '',
      maxWidth: 48,
      getter: (destination: TravelDestination) => destination.sequence,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.transportationHeader),
      getter: (destination: TravelDestination) => (
        <Space direction='horizontal' align='center' size='small'>
          {/* Only the flight glyph exists in the DS icon font — other
              transport modes render label-only (accepted gap, travels 2.1). */}
          {destination.transport === TravelTransportTypes.FLIGHT && (
            <IconAirfare size={16} />
          )}
          <Text autoMargin={false}>
            {intl.formatMessage(TRANSPORT_MESSAGES[destination.transport])}
          </Text>
        </Space>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.departureHeader),
      getter: (destination: TravelDestination) =>
        destination.departureAddress.displayLabel,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.departureDateHeader),
      getter: (destination: TravelDestination) => (
        <FormattedShortDate date={destination.departureDate} />
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.arrivalHeader),
      getter: (destination: TravelDestination) =>
        destination.arrivalAddress.displayLabel,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.arrivalDateHeader),
      getter: (destination: TravelDestination) => (
        <FormattedShortDate date={destination.arrivalDate} />
      ),
    }),
  ]

  const showStatusStamp = travel.isApproved || travel.isDenied
  const statusStampVariant = travel.isApproved ? 'approved' : 'denied'

  return (
    <Space size='large'>
      <Space direction='horizontal' justify='space-between' align='center'>
        <Header as='h2'>
          {intl.formatMessage(messages.travelHeading, {
            travelId: travel.id,
          })}
        </Header>
        <TravelApprovalHistoryPopover travelId={travel.id} logs={travel.logs} />
      </Space>

      <Space direction='horizontal'>
        <Space.Item width={showStatusStamp ? 1 / 3 : 1 / 2}>
          <Descriptions columns={1}>
            <Descriptions.Item
              label={intl.formatMessage(messages.requestedByLabel)}
            >
              <UserLabel user={{ fullName: travel.requesterFullName }} />
            </Descriptions.Item>
            {travel.primaryCategory && (
              <Descriptions.Item
                label={intl.formatMessage(messages.locationLabel)}
              >
                {travel.primaryCategory.name}
              </Descriptions.Item>
            )}
            {travel.secondaryCategory && (
              <Descriptions.Item
                label={intl.formatMessage(messages.departmentLabel)}
              >
                {travel.secondaryCategory.name}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Space.Item>

        <Space.Item width={showStatusStamp ? 1 / 3 : 1 / 2}>
          <Descriptions columns={1}>
            <Descriptions.Item
              label={intl.formatMessage(messages.submittedLabel)}
            >
              <FormattedShortDate date={travel.dateCreated} />
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage(messages.lastModifiedLabel)}
            >
              <FormattedShortDate date={travel.dateLastModified} />
            </Descriptions.Item>
            {travel.isPending && travel.nextApproverFullName && (
              <Descriptions.Item
                label={intl.formatMessage(messages.nextApproverLabel)}
              >
                <UserLabel user={{ fullName: travel.nextApproverFullName }} />
              </Descriptions.Item>
            )}
          </Descriptions>
        </Space.Item>

        {showStatusStamp && (
          <Space.Item width={1 / 3}>
            <Space align='center' size='small'>
              <StatusStampStyled $variant={statusStampVariant}>
                {intl.formatMessage(
                  travel.isApproved
                    ? messages.statusStampApproved
                    : messages.statusStampDenied
                )}
              </StatusStampStyled>
              <SubText bold autoMargin={false}>
                {intl.formatMessage(
                  travel.isApproved
                    ? messages.approvedCaption
                    : messages.deniedCaption
                )}
              </SubText>
            </Space>
          </Space.Item>
        )}
      </Space>

      <DataTable<TravelDestination>
        id={`travel-${travel.id}-itinerary`}
        aria-label={intl.formatMessage(messages.itineraryAriaLabel, {
          travelId: travel.id,
        })}
        data={travel.destinations}
        columns={itineraryColumns}
        customizableColumns={false}
        persistCustomizedColumns={false}
      />

      <Space direction='horizontal'>
        <Space.Item width={1 / 2}>
          <Space size='large'>
            <Space size='small'>
              <SubText bold color='textSecondary' autoMargin={false}>
                {intl.formatMessage(messages.travelPurposeLabel)}
              </SubText>
              <Text autoMargin={false}>{travel.purpose}</Text>
            </Space>
            <Space size='small'>
              <SubText bold color='textSecondary' autoMargin={false}>
                {intl.formatMessage(messages.travelDetailsLabel)}
              </SubText>
              <Text autoMargin={false}>
                {travel.hasDetails
                  ? travel.details
                  : intl.formatMessage(messages.noDetailsText)}
              </Text>
            </Space>
          </Space>
        </Space.Item>
        <Space.Item width={1 / 2}>
          <Space direction='horizontal' justify='end' align='center'>
            <Header as='h4'>
              {intl.formatMessage(messages.requestedBudgetLabel)}
            </Header>
            <Price
              price={travel.travelBudget}
              currency={travel.currency.name}
              fontSize={24}
            />
          </Space>
        </Space.Item>
      </Space>
    </Space>
  )
}

TravelDetailContent.displayName = 'TravelDetailContent'
